import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, FormEvent, useMemo } from 'react';
import { Product, PaginatedResponse, OrderContactInfo, getErrorMessage } from '../types';
import api, { getAccessToken } from '../api';
import { ImageCarousel } from './ImageCarousel';
import { ImageLightbox } from './ImageLightbox';
import { Login } from './Login';
import { useTranslation } from '../i18n';
import { LenderMetaRow } from './LenderMetaRow';
import { ReportContentButton } from './ReportContentButton';
import { useApiRequest } from '../useApiRequest';
import { Tooltip } from './Tooltip';
import { Modal } from './Modal';
import { LocationMap, LatLng } from './LocationMap';
import { ProductFilters } from './filters/ProductFilters';
import { LocationFilter } from './filters/LocationFilter';
import { DateFilter } from './filters/DateFilter';
import { OwnerFilter } from './filters/OwnerFilter';
import {
  DEFAULT_SEARCH_RADIUS_KM,
  PRAGUE_CENTER,
  resolveDeviceSearchCenterIfGranted,
  SearchLocationSource,
} from '../utils/searchLocation';
import {
  countRentalDays,
  renterSubtotal,
  rondoshareCommission,
  rentalTotal,
  effectivePerDay,
  ProductCategory,
} from '../pricing';
import { categoryLabelKey, hasCategoryFilter } from '../categories';
import { accountTabForMessage, isVerificationRequiredApiError } from '../accountAction';
import { AccountActionModal } from './AccountActionModal';
import { FormLabel } from './FormLabel';
import { FieldError } from './FieldError';
import { validateFormSubmit } from '../utils/formValidation';
import { RentalDateRangePicker } from './RentalDateRangePicker';
import { SearchResultsMap, productHasMapLocation } from './SearchResultsMap';
import { formatDateOnly } from '../formatDate';

const PAGE_SIZE = 20;
const MAP_PAGE_SIZE = 100;
const DEBOUNCE_MS = 300;
const LOG_QUIET_MS = 1500;
const VIEW_MODE_STORAGE_KEY = 'rondoshare_search_view_mode';

type SearchViewMode = 'list' | 'map';

/** Rent modal steps: dates and price, handover message, summary with legal terms. */
type RentStep = 1 | 2 | 3;
const RENT_STEPS: RentStep[] = [1, 2, 3];

function readStoredViewMode(): SearchViewMode {
  try {
    return sessionStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'map' ? 'map' : 'list';
  } catch {
    return 'list';
  }
}

interface ProductListProps {
  token: string | null;
  userId: number | null;
  isVerified: boolean;
  onLogin: (accessToken: string, refreshToken: string) => void;
  setError: (error: string) => void;
  category?: ProductCategory;
  ownerId?: number;
}

export const ProductList: React.FC<ProductListProps> = ({ token, userId, isVerified, onLogin, setError, category, ownerId }) => {
  const { t, lang } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [featuredPage, setFeaturedPage] = useState(1);
  const [featuredTotalCount, setFeaturedTotalCount] = useState(0);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rentingProduct, setRentingProduct] = useState<Product | null>(null);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [detailLightboxIndex, setDetailLightboxIndex] = useState<number | null>(null);
  const [lenderApproximate, setLenderApproximate] = useState<LatLng | null>(null);
  const [loginProduct, setLoginProduct] = useState<Product | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
  const [searchDateError, setSearchDateError] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [searchString, setSearchString] = useState('');
  const [localOwner, setLocalOwner] = useState('');
  const [ownerUsername, setOwnerUsername] = useState('');
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [searchCenter, setSearchCenter] = useState<LatLng>(PRAGUE_CENTER);
  const [maxDistanceKm, setMaxDistanceKm] = useState(DEFAULT_SEARCH_RADIUS_KM);
  const [locationSource, setLocationSource] = useState<SearchLocationSource>('prague');
  const [modalError, setModalError] = useState('');
  const [accountActionMessage, setAccountActionMessage] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [dateOrderError, setDateOrderError] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [renterNotes, setRenterNotes] = useState('');
  const [rentalTermsHtml, setRentalTermsHtml] = useState('');
  const [rentStep, setRentStep] = useState<RentStep>(1);
  const [renterContact, setRenterContact] = useState<OrderContactInfo | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [wishlistDone, setWishlistDone] = useState(false);
  const [wishlistError, setWishlistError] = useState('');
  const [loginForWishlist, setLoginForWishlist] = useState(false);
  const [viewMode, setViewMode] = useState<SearchViewMode>(readStoredViewMode);
  const [mapProducts, setMapProducts] = useState<Product[]>([]);
  const [mapProductsLoading, setMapProductsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const ownerDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const logTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingLogSearchRef = useRef('');
  const rentFormRef = useRef<HTMLFormElement>(null);
  const ownerMode = ownerId != null;

  // The rent modal opens from the detail modal, so both show the same lender location.
  const locationProduct = detailProduct ?? rentingProduct;

  useEffect(() => {
    if (!locationProduct) {
      setLenderApproximate(null);
      return;
    }

    const lat = locationProduct.lenderApproximateLatitude;
    const lng = locationProduct.lenderApproximateLongitude;
    if (lat != null && lng != null) {
      setLenderApproximate({ lat, lng });
      return;
    }

    let cancelled = false;
    api
      .get<{ lenderApproximateLatitude?: number | null; lenderApproximateLongitude?: number | null }>(
        `/products/${locationProduct.id}/lender-location`
      )
      .then(res => {
        if (cancelled) return;
        const fetchedLat = res.data.lenderApproximateLatitude;
        const fetchedLng = res.data.lenderApproximateLongitude;
        if (fetchedLat != null && fetchedLng != null) {
          setLenderApproximate({ lat: fetchedLat, lng: fetchedLng });
        } else {
          setLenderApproximate(null);
        }
      })
      .catch(() => {
        if (!cancelled) setLenderApproximate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [locationProduct]);

  const openDetailProduct = useCallback((product: Product) => {
    setDetailProduct(product);
    if (product.lenderApproximateLatitude != null && product.lenderApproximateLongitude != null) {
      setLenderApproximate({
        lat: product.lenderApproximateLatitude,
        lng: product.lenderApproximateLongitude,
      });
    }
  }, []);

  const handleViewModeChange = useCallback((mode: SearchViewMode) => {
    setViewMode(mode);
    try {
      sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore storage errors
    }
  }, []);
  const openRentFlow = (product: Product) => {
    setModalError('');
    setDetailProduct(null);
    if (!token) {
      setLoginProduct(product);
      return;
    }
    if (!isVerified) {
      setAccountActionMessage(t('verification.rent_blocked'));
      return;
    }
    setRentingProduct(product);
    setStartDate(searchStartDate);
    setEndDate(searchEndDate);
    setDateOrderError('');
    setRenterNotes('');
    setAcceptedTerms(false);
    setShowTerms(false);
  };

  const { loading: submittingOrder, call: submitOrderRequest } = useApiRequest(async () => {
    if (!rentingProduct) return;
    const payload = {
      rentalStartDate: new Date(startDate).toISOString(),
      rentalEndDate: new Date(endDate).toISOString(),
      items: [{ productId: rentingProduct.id, quantity: 1 }],
      successUrl: `${window.location.origin}/payment-success`,
      cancelUrl: `${window.location.origin}?payment=cancelled`,
      acceptedTerms: true,
      renterNotes: renterNotes.trim()
    };
    const res = await api.post<{ url: string }>(
      `/orders/create-checkout-session`, payload
    );
    window.location.href = res.data.url;
  });

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    if (value.trim() || localOwner.trim()) {
      setSearchInitiated(true);
    }
    setWishlistDone(false);
    setWishlistError('');
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchString(value);
      setPage(1);
    }, DEBOUNCE_MS);
  };

  const handleOwnerChange = (value: string) => {
    setLocalOwner(value);
    if (value.trim() || localSearch.trim()) {
      setSearchInitiated(true);
    }
    setWishlistDone(false);
    setWishlistError('');
    clearTimeout(ownerDebounceRef.current);
    ownerDebounceRef.current = setTimeout(() => {
      setOwnerUsername(value);
      setPage(1);
    }, DEBOUNCE_MS);
  };

  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    clearTimeout(ownerDebounceRef.current);
    clearTimeout(logTimerRef.current);
  }, []);

  useEffect(() => {
    if (category == null) return;
    setPage(1);
    setLocalSearch('');
    setSearchString('');
    setSearchInitiated(false);
    setWishlistDone(false);
    setWishlistError('');
    setFeaturedProducts([]);
    setFeaturedPage(1);
    setFeaturedTotalCount(0);
  }, [category]);

  const scheduleSearchLog = useCallback((search: string) => {
    const trimmed = search.trim();
    if (!trimmed) return;

    pendingLogSearchRef.current = trimmed;
    clearTimeout(logTimerRef.current);
    logTimerRef.current = setTimeout(() => {
      const q = pendingLogSearchRef.current;
      if (!q) return;
      api.post('/search/log', { searchString: q, category: category ?? null }).catch(() => {});
    }, LOG_QUIET_MS);
  }, [category]);

  useEffect(() => {
    let cancelled = false;
    resolveDeviceSearchCenterIfGranted().then(result => {
      if (cancelled || !result) return;
      setSearchCenter(result.center);
      setLocationSource('device');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setSearchCenter(PRAGUE_CENTER);
      setLocationSource('prague');
      setPage(1);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setSearchCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationSource('device');
        setPage(1);
      },
      () => {
        setSearchCenter(PRAGUE_CENTER);
        setLocationSource('prague');
        setPage(1);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    if (rentingProduct && !rentalTermsHtml) {
      api.get<{ html: string }>('/contracts/rental-terms')
        .then(res => setRentalTermsHtml(res.data.html))
        .catch(() => {});
    }
    if (!rentingProduct) {
      setAcceptedTerms(false);
      setShowTerms(false);
    }
  }, [rentingProduct, rentalTermsHtml]);

  useEffect(() => {
    setRentStep(1);
    if (!rentingProduct) return;
    let cancelled = false;
    // Shown in the handover step so the renter knows their contact details are shared automatically.
    api.get<{ firstName?: string; lastName?: string; email?: string; phoneNumber?: string }>('/users/settings')
      .then(res => {
        if (cancelled) return;
        setRenterContact({
          trueName: `${res.data.firstName ?? ''} ${res.data.lastName ?? ''}`.trim() || undefined,
          email: res.data.email,
          phone: res.data.phoneNumber,
        });
      })
      .catch(() => {
        if (!cancelled) setRenterContact(null);
      });
    return () => {
      cancelled = true;
    };
  }, [rentingProduct]);

  const searchRentalDays = useMemo(() => {
    if (!searchStartDate || !searchEndDate || searchDateError) return null;
    return countRentalDays(searchStartDate, searchEndDate);
  }, [searchStartDate, searchEndDate, searchDateError]);

  const appendRentalDateParams = useCallback((params: URLSearchParams) => {
    if (searchRentalDays !== null) {
      params.set('rentalStartDate', searchStartDate);
      params.set('rentalEndDate', searchEndDate);
    }
  }, [searchRentalDays, searchStartDate, searchEndDate]);

  const handleSearchStartDateChange = (value: string) => {
    setSearchStartDate(value);
    setPage(1);
    if (value && searchEndDate && value > searchEndDate) {
      setSearchDateError(t('products.date_range_error'));
    } else {
      setSearchDateError('');
    }
  };

  const handleSearchEndDateChange = (value: string) => {
    setSearchEndDate(value);
    setPage(1);
    if (searchStartDate && value && value < searchStartDate) {
      setSearchDateError(t('products.date_range_error'));
    } else {
      setSearchDateError('');
    }
  };

  const fetchProducts = useCallback(async (
    search: string,
    pg: number,
    center: LatLng,
    radiusKm: number,
    categoryFilter?: ProductCategory,
    createdBy?: number,
    ownerFilter?: string,
  ) => {
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({
        searchString: search,
        page: String(pg),
        pageSize: String(PAGE_SIZE),
      });
      if (createdBy == null) {
        params.set('nearLatitude', String(center.lat));
        params.set('nearLongitude', String(center.lng));
        params.set('maxDistanceKm', String(radiusKm));
      } else {
        params.set('createdById', String(createdBy));
      }
      if (categoryFilter != null) {
        params.set('category', String(categoryFilter));
      }
      if (ownerFilter?.trim()) {
        params.set('ownerUsername', ownerFilter.trim());
      }
      appendRentalDateParams(params);
      const res = await api.get<PaginatedResponse<Product>>(`/products?${params}`);
      setProducts(res.data.items);
      setTotalCount(res.data.totalCount);
      if (search.trim()) {
        scheduleSearchLog(search);
      }
    } catch (err) { setError(getErrorMessage(err, t('products.load_error'))); }
    finally { setSearchLoading(false); }
  }, [setError, t, scheduleSearchLog, appendRentalDateParams]);

  const fetchMapProducts = useCallback(async (
    search: string,
    center: LatLng,
    radiusKm: number,
    categoryFilter?: ProductCategory,
    ownerFilter?: string,
  ) => {
    setMapProductsLoading(true);
    try {
      const params = new URLSearchParams({
        searchString: search,
        page: '1',
        pageSize: String(MAP_PAGE_SIZE),
        nearLatitude: String(center.lat),
        nearLongitude: String(center.lng),
        maxDistanceKm: String(radiusKm),
      });
      if (categoryFilter != null) {
        params.set('category', String(categoryFilter));
      }
      if (ownerFilter?.trim()) {
        params.set('ownerUsername', ownerFilter.trim());
      }
      appendRentalDateParams(params);
      const res = await api.get<PaginatedResponse<Product>>(`/products?${params}`);
      setMapProducts(res.data.items);
    } catch (err) { setError(getErrorMessage(err, t('products.load_error'))); }
    finally { setMapProductsLoading(false); }
  }, [setError, t, appendRentalDateParams]);

  const fetchFeaturedProducts = useCallback(async (pageNum: number) => {
    setFeaturedLoading(true);
    try {
      const params = new URLSearchParams({
        featuredOnly: 'true',
        page: String(pageNum),
        pageSize: String(PAGE_SIZE),
      });
      appendRentalDateParams(params);
      const res = await api.get<PaginatedResponse<Product>>(`/products?${params}`);
      setFeaturedProducts(res.data.items);
      setFeaturedTotalCount(res.data.totalCount);
    } catch (err) { setError(getErrorMessage(err, t('products.load_error'))); }
    finally { setFeaturedLoading(false); }
  }, [setError, t, appendRentalDateParams]);

  useEffect(() => {
    if (ownerMode) {
      fetchProducts('', page, searchCenter, maxDistanceKm, undefined, ownerId);
      return;
    }
    if (!hasCategoryFilter(category) && !searchString.trim() && !ownerUsername.trim()) {
      setProducts([]);
      setTotalCount(0);
      setSearchLoading(false);
      return;
    }
    setWishlistDone(false);
    setWishlistError('');
    fetchProducts(searchString, page, searchCenter, maxDistanceKm, category, undefined, ownerUsername);
  }, [fetchProducts, searchString, page, searchCenter, maxDistanceKm, category, searchRentalDays, ownerMode, ownerId, ownerUsername]);

  useEffect(() => {
    if (ownerMode || viewMode !== 'map') return;
    if (!hasCategoryFilter(category) && !searchString.trim() && !ownerUsername.trim()) {
      setMapProducts([]);
      setMapProductsLoading(false);
      return;
    }
    fetchMapProducts(searchString, searchCenter, maxDistanceKm, category, ownerUsername);
  }, [viewMode, fetchMapProducts, searchString, searchCenter, maxDistanceKm, category, searchRentalDays, ownerMode, ownerUsername]);

  const showWishlistPrompt =
    !ownerMode
    && !searchLoading
    && searchString.trim().length > 0
    && totalCount === 0
    && (category != null || searchInitiated);

  const handleWishlistSearch = async () => {
    setWishlistError('');
    if (!getAccessToken()) {
      setLoginForWishlist(true);
      return;
    }
    try {
      await api.post('/search/wishlist', {
        searchString: searchString.trim(),
        category: category ?? null,
      });
      setWishlistDone(true);
    } catch (err) {
      setWishlistError(getErrorMessage(err, t('search_wishlist.error')));
    }
  };

  useEffect(() => {
    setFeaturedPage(1);
  }, [searchRentalDays]);

  useEffect(() => {
    if (ownerMode || hasCategoryFilter(category) || searchInitiated) return;
    fetchFeaturedProducts(featuredPage);
  }, [fetchFeaturedProducts, searchInitiated, category, searchRentalDays, ownerMode, featuredPage]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const featuredTotalPages = Math.ceil(featuredTotalCount / PAGE_SIZE);
  const showViewToggle = !ownerMode && (hasCategoryFilter(category) || searchString.trim().length > 0 || ownerUsername.trim().length > 0);
  const mappableMapProducts = useMemo(
    () => mapProducts.filter(productHasMapLocation),
    [mapProducts],
  );

  const renderPagination = (currentPage: number, pageCount: number, onPageChange: (page: number) => void) => {
    if (pageCount <= 1) return null;
    return (
      <div className="pagination">
        <button
          className="btn btn-secondary btn-sm"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          {t('products.page_prev')}
        </button>
        {Array.from({ length: pageCount }, (_, i) => i + 1)
          .filter(p => p === 1 || p === pageCount || Math.abs(p - currentPage) <= 2)
          .reduce<(number | '...')[]>((acc, p) => {
            const last = acc[acc.length - 1];
            if (typeof last === 'number' && p - last > 1) acc.push('...');
            acc.push(p);
            return acc;
          }, [])
          .map((p, i) =>
            p === '...' ? (
              <span key={`ellipsis-${i}`} className="pagination-ellipsis">&hellip;</span>
            ) : (
              <button
                key={p}
                className={`btn btn-sm ${p === currentPage ? 'btn-accent' : 'btn-secondary'}`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
            )
          )}
        <button
          className="btn btn-secondary btn-sm"
          disabled={currentPage >= pageCount}
          onClick={() => onPageChange(currentPage + 1)}
        >
          {t('products.page_next')}
        </button>
      </div>
    );
  };

  const formatProductPrice = useCallback((product: Product) => {
    if (searchRentalDays !== null) {
      const fixed = product.renterFixedFee ?? 0;
      const daily = product.renterFeePerDay ?? product.price;
      const total = rentalTotal(fixed, daily, searchRentalDays, 1);
      return t('products.price_total', { total });
    }
    return t('products.price_fixed_daily', {
      fixed: product.renterFixedFee ?? 0,
      daily: product.renterFeePerDay ?? product.price,
    });
  }, [t, searchRentalDays]);

  const rentalPricePreview = useMemo(() => {
    if (!rentingProduct) return null;
    const days = countRentalDays(startDate, endDate);
    if (days === null) return null;
    const fixed = rentingProduct.renterFixedFee ?? 0;
    const daily = rentingProduct.renterFeePerDay ?? rentingProduct.price;
    const subtotal = renterSubtotal(fixed, daily, days, 1);
    const commission = rondoshareCommission(subtotal);
    const total = rentalTotal(fixed, daily, days, 1);
    return {
      days,
      fixed,
      daily,
      subtotal,
      commission,
      total,
      perDay: effectivePerDay(total, days),
    };
  }, [rentingProduct, startDate, endDate]);

  useLayoutEffect(() => {
    rentFormRef.current?.closest('.modal__body')?.scrollTo(0, 0);
  }, [rentStep]);

  const renderRentalPriceSummary = (product: Product) => (
    <div className="rental-price-summary" aria-live="polite">
      <div className="rental-price-summary__rate">
        {formatProductPrice(product)}
      </div>
      {rentalPricePreview ? (
        <div className="price-breakdown">
          <div className="price-breakdown__row">
            <span>{t('rental.price_breakdown_rental')}</span>
            <span>
              {t('rental.price_breakdown_rental_detail', {
                fixed: rentalPricePreview.fixed,
                daily: rentalPricePreview.daily,
                days: rentalPricePreview.days,
                subtotal: rentalPricePreview.subtotal,
              })}
            </span>
          </div>
          <div className="price-breakdown__row">
            <span>{t('rental.price_breakdown_platform')}</span>
            <span>{rentalPricePreview.commission} Kč</span>
          </div>
          <div className="price-breakdown__row price-breakdown__row--total">
            <span>{t('rental.price_breakdown_total')}</span>
            <span>
              {t('rental.price_breakdown_total_detail', {
                total: rentalPricePreview.total,
                perDay: rentalPricePreview.perDay,
              })}
            </span>
          </div>
        </div>
      ) : (
        <p className="rental-price-summary__hint">
          {t('rental.price_select_dates')}
        </p>
      )}
    </div>
  );

  const submitOrder = async (e: FormEvent) => {
    e.preventDefault();
    if (!rentingProduct) return;
    setModalError('');
    setTermsError('');
    setDateOrderError('');
    if (!validateFormSubmit(e)) return;
    if (!startDate || !endDate) {
      setRentStep(1);
      setDateOrderError(t('rental.price_select_dates'));
      return;
    }
    if (endDate < startDate) {
      setRentStep(1);
      setDateOrderError(t('rental.end_before_start'));
      return;
    }
    if (rentStep < 3) {
      // Each step validates only its own fields, so the next one starts without error highlights.
      (e.currentTarget as HTMLFormElement).classList.remove('form-submitted');
      setRentStep(rentStep === 1 ? 2 : 3);
      return;
    }
    if (!acceptedTerms) {
      setTermsError(t('rental.terms_required'));
      return;
    }
    try {
      await submitOrderRequest();
    } catch (err) {
      if (isVerificationRequiredApiError(err)) {
        setRentingProduct(null);
        setAccountActionMessage(t('verification.rent_blocked'));
      } else {
        setModalError(getErrorMessage(err, t('rental.checkout_error')));
      }
    }
  };

  const renderProductTile = (p: Product) => (
    <div
      key={p.id}
      className="product-tile"
      role="button"
      tabIndex={0}
      aria-label={t('products.view_detail')}
      onClick={() => openDetailProduct(p)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDetailProduct(p);
        }
      }}
    >
      <div className="tile-image">
        <ImageCarousel
          images={p.images}
          size={999}
          fill
        />
      </div>
      <div className="tile-body">
        <h3>{p.name}</h3>
        <p className="tile-desc">{p.description}</p>
        <div className="tile-footer">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div className="tile-price">{formatProductPrice(p)}</div>
              <Tooltip content={t('products.guarantee_tooltip')}>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className="guarantee-icon-link"
                  aria-label={t('products.guarantee_label')}
                >
                  <img src="/guarantee.png" alt="" className="guarantee-icon" />
                </button>
              </Tooltip>
              
            </div>
            {p.createdByUsername && (
              <LenderMetaRow
                username={p.createdByUsername}
                level={p.lenderLevel ?? 1}
                averageRating={p.lenderAverageRating ?? 0}
                ratingCount={p.lenderRatingCount ?? 0}
                userId={p.createdById}
              />
            )}
          </div>
          {userId != null && p.createdById === userId ? (
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>{t('products.own')}</span>
          ) : (
            <button
              className="btn btn-accent btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                openRentFlow(p);
              }}
            >
              {t('products.rent')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {!ownerMode && (
        <>
          <h2 className="section-title">
            {category != null
              ? t('categories.products_in', { category: t(categoryLabelKey(category)) })
              : t('products.search_results')}
          </h2>
          <div className="search-bar">
            <input
              type="text"
              placeholder={t('products.search')}
              value={localSearch}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </div>
          <ProductFilters>
            <LocationFilter
              center={searchCenter}
              radiusKm={maxDistanceKm}
              locationSource={locationSource}
              onCenterChange={point => {
                setSearchCenter(point);
                setLocationSource('custom');
                setPage(1);
              }}
              onRadiusChange={km => {
                setMaxDistanceKm(km);
                setPage(1);
              }}
              onUseMyLocation={useMyLocation}
            />
            <DateFilter
              startDate={searchStartDate}
              endDate={searchEndDate}
              rangeError={searchDateError}
              onStartDateChange={handleSearchStartDateChange}
              onEndDateChange={handleSearchEndDateChange}
            />
            <OwnerFilter
              value={localOwner}
              onChange={handleOwnerChange}
            />
          </ProductFilters>

          {showViewToggle && (
            <div className="view-toggle" role="group" aria-label={t('products.search_results')}>
              <button
                type="button"
                className={`view-toggle__btn${viewMode === 'list' ? ' view-toggle__btn--active' : ''}`}
                aria-pressed={viewMode === 'list'}
                onClick={() => handleViewModeChange('list')}
              >
                {t('products.view_list')}
              </button>
              <button
                type="button"
                className={`view-toggle__btn${viewMode === 'map' ? ' view-toggle__btn--active' : ''}`}
                aria-pressed={viewMode === 'map'}
                onClick={() => handleViewModeChange('map')}
              >
                {t('products.view_map')}
              </button>
            </div>
          )}
        </>
      )}

      {viewMode === 'list' || ownerMode ? (
        <div className="product-grid">
          {products.map(renderProductTile)}
        </div>
      ) : showViewToggle && (
        <>
          {totalCount > MAP_PAGE_SIZE && (
            <p className="map-results-notice text-muted">
              {t('products.map_truncated', { shown: MAP_PAGE_SIZE, total: totalCount })}
            </p>
          )}
          <SearchResultsMap
            products={mapProducts}
            searchCenter={searchCenter}
            radiusKm={maxDistanceKm}
            selectedProductId={detailProduct?.id}
            onSelectProduct={openDetailProduct}
            formatPrice={formatProductPrice}
            loading={mapProductsLoading}
          />
          {!mapProductsLoading && mapProducts.length > 0 && mappableMapProducts.length === 0 && (
            <p className="text-muted">{t('products.map_no_locations')}</p>
          )}
        </>
      )}

      {ownerMode && totalCount === 0 && !searchLoading && (
        <p className="text-muted">{t('user.no_items')}</p>
      )}

      {!ownerMode && showViewToggle && totalCount === 0 && !searchLoading && (
        <p className="text-muted">
          {category != null && !searchString.trim() && !ownerUsername.trim()
            ? t('categories.none')
            : localSearch.trim() || searchString.trim() || localOwner.trim() || ownerUsername.trim()
              ? t('products.none')
              : t('products.search_prompt')}
        </p>
      )}

      {showWishlistPrompt && (
        <div className="search-wishlist-panel">
          {wishlistDone ? (
            <p className="search-wishlist-panel__success">{t('search_wishlist.success')}</p>
          ) : (
            <>
              <p className="search-wishlist-panel__intro">{t('search_wishlist.intro')}</p>
              {wishlistError && (
                <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
                  {wishlistError}
                  <button type="button" className="dismiss" onClick={() => setWishlistError('')}>&times;</button>
                </div>
              )}
              <button
                type="button"
                className="btn btn-accent btn-sm"
                onClick={handleWishlistSearch}
              >
                {t('search_wishlist.button', { query: searchString.trim() })}
              </button>
            </>
          )}
        </div>
      )}

      {(showViewToggle || ownerMode) && renderPagination(page, totalPages, setPage)}

      {!ownerMode && !hasCategoryFilter(category) && !searchInitiated && (featuredLoading || featuredTotalCount > 0) && (
        <>
          <h2 className="section-title">{t('products.title')}</h2>
          {featuredProducts.length > 0 && (
            <div className="product-grid">
              {featuredProducts.map(renderProductTile)}
            </div>
          )}
          {!featuredLoading && featuredTotalCount === 0 && (
            <p className="text-muted">{t('products.none')}</p>
          )}
          {renderPagination(featuredPage, featuredTotalPages, setFeaturedPage)}
        </>
      )}

      <Modal
        open={detailProduct != null}
        onClose={() => {
          setDetailLightboxIndex(null);
          setDetailProduct(null);
        }}
        title={detailProduct?.name}
        panelClassName="modal--scroll"
      >
        {detailProduct && (
          <>
            <div className="product-detail-modal__image">
              <ImageCarousel
                images={detailProduct.images}
                size={999}
                fill
                objectFit="contain"
                quality="original"
                onImageClick={index => setDetailLightboxIndex(index)}
                imageClickLabel={t('ui.lightbox_open')}
              />
            </div>
            <p className="product-detail-modal__desc">{detailProduct.description}</p>
            <div className="guarantee-banner">
              <img src="/guarantee.png" alt="" />
              <div>
                <div>{t('products.guarantee_label')}</div>
                <div style={{ fontWeight: 500, marginTop: 4 }}>
                  <a href="/guarantee">{t('manage.fee_guarantee_link')}</a>
                </div>
              </div>
            </div>
            {lenderApproximate && (
              <div style={{ margin: '0 0 0.75rem' }}>
                <p className="text-muted" style={{ fontSize: '0.9rem', margin: '0 0 0.35rem' }}>
                  {t('products.lender_location')}
                </p>
                <LocationMap approximate={lenderApproximate} height={220} interactive={false} />
              </div>
            )}
            <div className="tile-footer" style={{ marginTop: 0 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div className="tile-price">{formatProductPrice(detailProduct)}</div>
                  <img src="/guarantee.png" alt="" className="guarantee-icon" />
                </div>
                {detailProduct.createdByUsername && (
                  <LenderMetaRow
                    username={detailProduct.createdByUsername}
                    level={detailProduct.lenderLevel ?? 1}
                    averageRating={detailProduct.lenderAverageRating ?? 0}
                    ratingCount={detailProduct.lenderRatingCount ?? 0}
                    userId={detailProduct.createdById}
                  />
                )}
              </div>
              {userId != null && detailProduct.createdById === userId ? (
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>{t('products.own')}</span>
              ) : (
                <button
                  type="button"
                  className="btn btn-accent btn-sm"
                  onClick={() => openRentFlow(detailProduct)}
                >
                  {t('products.rent')}
                </button>
              )}
            </div>
            {(userId == null || detailProduct.createdById !== userId) && (
              <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                <ReportContentButton productId={detailProduct.id} />
              </div>
            )}
          </>
        )}
      </Modal>

      <ImageLightbox
        open={detailProduct != null && detailLightboxIndex != null}
        onClose={() => setDetailLightboxIndex(null)}
        images={detailProduct?.images ?? []}
        initialIndex={detailLightboxIndex ?? 0}
        alt={detailProduct?.name}
      />

      <Modal
        open={rentingProduct != null}
        onClose={() => {
          setModalError('');
          setRentingProduct(null);
        }}
        title={rentingProduct ? t('rental.title', { name: rentingProduct.name }) : undefined}
        panelClassName="modal--scroll"
      >
        {rentingProduct && (
          <>
            {modalError && (
              <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
                {modalError}
                <button type="button" className="dismiss" onClick={() => setModalError('')}>&times;</button>
              </div>
            )}
            <form ref={rentFormRef} onSubmit={submitOrder}>
              <ol className="rent-steps" aria-label={t('rental.steps_label')}>
                {RENT_STEPS.map(step => (
                  <li
                    key={step}
                    className={`rent-steps__item${step === rentStep ? ' rent-steps__item--current' : ''}${step < rentStep ? ' rent-steps__item--done' : ''}`}
                    aria-current={step === rentStep ? 'step' : undefined}
                  >
                    <span className="rent-steps__number">{step}</span>
                    {t(`rental.step_${step}`)}
                  </li>
                ))}
              </ol>

              {rentStep === 1 && (
                <>
                  <RentalDateRangePicker
                    productId={rentingProduct.id}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(start, end) => {
                      setStartDate(start);
                      setEndDate(end);
                    }}
                    disabled={submittingOrder}
                    rangeError={dateOrderError}
                    onRangeError={setDateOrderError}
                  />
                  {renderRentalPriceSummary(rentingProduct)}
                  <ul className="rent-assurances">
                    <li className="rent-assurances__item rent-assurances__item--guarantee">
                      <img src="/guarantee.png" alt="" />
                      <span>
                        {t('rental.assurance_guarantee')}{' '}
                        <a href="/guarantee" target="_blank" rel="noopener noreferrer">{t('manage.fee_guarantee_link')}</a>
                      </span>
                    </li>
                    <li className="rent-assurances__item">{t('rental.assurance_payment_hold')}</li>
                    <li className="rent-assurances__item">{t('rental.assurance_cancellation')}</li>
                  </ul>
                  <div className="rent-location">
                    <p className="rent-location__title">{t('rental.location_title')}</p>
                    <p className="rent-location__hint">{t('rental.location_hint')}</p>
                    {lenderApproximate && (
                      <LocationMap approximate={lenderApproximate} height={160} interactive={false} />
                    )}
                  </div>
                </>
              )}

              {rentStep === 2 && (
                <>
                  <div className="form-group">
                    <FormLabel required htmlFor="renterNotes">
                      {t('rental.notes_label')}{' '}
                      <Tooltip content={t('rental.notes_help')} />
                    </FormLabel>
                    <textarea
                      id="renterNotes"
                      className="form-input"
                      rows={4}
                      value={renterNotes}
                      onChange={e => setRenterNotes(e.target.value)}
                      placeholder={t('rental.notes_placeholder')}
                      maxLength={500}
                      required
                      disabled={submittingOrder}
                      aria-required="true"
                      autoFocus
                    />
                  </div>
                  <div className="rent-contact-preview">
                    <p className="rent-contact-preview__title">{t('rental.contact_preview_title')}</p>
                    {renterContact && (renterContact.trueName || renterContact.email || renterContact.phone) && (
                      <ul className="rent-contact-preview__list">
                        {renterContact.trueName && <li>{renterContact.trueName}</li>}
                        {renterContact.phone && <li>{renterContact.phone}</li>}
                        {renterContact.email && <li>{renterContact.email}</li>}
                      </ul>
                    )}
                    <a href="/account" target="_blank" rel="noopener noreferrer" className="rent-contact-preview__edit">
                      {t('rental.contact_preview_edit')}
                    </a>
                  </div>
                </>
              )}

              {rentStep === 3 && (
                <>
                  <dl className="rent-summary">
                    <dt>{t('rental.summary_dates')}</dt>
                    <dd>{formatDateOnly(startDate, lang)} – {formatDateOnly(endDate, lang)}</dd>
                    {rentalPricePreview && (
                      <>
                        <dt>{t('rental.summary_total')}</dt>
                        <dd>{rentalPricePreview.total} Kč</dd>
                      </>
                    )}
                    <dt>{t('rental.summary_notes')}</dt>
                    <dd className="rent-summary__notes">{renterNotes.trim()}</dd>
                  </dl>
                  <div className="rent-legal">
                    <p className="rent-legal__title">{t('rental.legal_title')}</p>
                    <ul className="rent-legal__list">
                      {/* Business lenders cannot list items (OfferIssue.BusinessSeller), so this holds for every rental. */}
                      <li>{t('rental.private_lender_notice')}</li>
                      <li>{t('rental.card_saving_notice')}</li>
                      <li>{t('rental.immediate_service_notice')}</li>
                    </ul>
                  </div>
                  <button
                    type="button"
                    className="btn-link rent-terms-toggle"
                    onClick={() => setShowTerms(v => !v)}
                    aria-expanded={showTerms}
                  >
                    {showTerms ? t('rental.terms_toggle_hide') : t('rental.terms_toggle')}
                  </button>
                  {showTerms && rentalTermsHtml && (
                    <div
                      className="rental-terms-preview"
                      style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--color-border, #ddd)', borderRadius: '6px', padding: '0.75rem', fontSize: '0.82rem', marginBottom: '0.5rem', background: 'var(--color-surface, #fafafa)' }}
                      dangerouslySetInnerHTML={{ __html: rentalTermsHtml }}
                    />
                  )}
                  <div className={`form-group${termsError ? ' form-group--error' : ''}`} style={{ marginBottom: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={e => { setAcceptedTerms(e.target.checked); setTermsError(''); }}
                        disabled={submittingOrder}
                        aria-invalid={termsError ? true : undefined}
                        aria-describedby={termsError ? 'rental-terms-error' : undefined}
                      />
                      {t('rental.terms_label')}
                    </label>
                    <FieldError id="rental-terms-error" message={termsError} />
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-accent" disabled={submittingOrder}>
                  {submittingOrder && <span className="btn-spinner" aria-hidden="true" />}
                  {rentStep === 3 ? t('rental.submit') : t('rental.next')}
                </button>
                {rentStep === 1 ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setModalError('');
                      setRentingProduct(null);
                    }}
                  >
                    {t('rental.cancel')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={submittingOrder}
                    onClick={() => setRentStep(rentStep === 3 ? 2 : 1)}
                  >
                    {t('rental.back')}
                  </button>
                )}
              </div>
            </form>
          </>
        )}
      </Modal>

      <AccountActionModal
        open={accountActionMessage != null}
        onClose={() => setAccountActionMessage(null)}
        message={accountActionMessage ?? ''}
        tab={accountTabForMessage(accountActionMessage ?? '', t)}
      />

      <Modal open={loginProduct != null} onClose={() => setLoginProduct(null)} ariaLabel={t('nav.signin')}>
        {loginProduct && (
          <Login
            inline
            onLogin={onLogin}
            setError={setError}
            onLoginSuccess={() => {
              setLoginProduct(null);
              setRentingProduct(loginProduct);
              setStartDate(searchStartDate);
              setEndDate(searchEndDate);
            }}
          />
        )}
      </Modal>

      <Modal open={loginForWishlist} onClose={() => setLoginForWishlist(false)} ariaLabel={t('nav.signin')}>
        <Login
          inline
          onLogin={onLogin}
          setError={setError}
          onLoginSuccess={() => {
            setLoginForWishlist(false);
            void handleWishlistSearch();
          }}
        />
      </Modal>
    </div>
  );
};
